import { createFileRoute, Link } from "@tanstack/react-router";
import { useRef, useState, type FormEvent } from "react";
import { z } from "zod";

import { Logo } from "@/components/Logo";
import {
  TurnstileWidget,
  type TurnstileHandle,
} from "@/components/security/TurnstileWidget";
import { requestAccessLink } from "@/lib/access-link.functions";

export const Route = createFileRoute("/cockpit_/passwort-vergessen")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Passwort zurücksetzen – creategy" },
      { name: "description", content: "Passwort für Ihren creategy-Cockpit-Zugang zurücksetzen." },
      { property: "og:title", content: "Passwort zurücksetzen – creategy" },
      { property: "og:description", content: "Passwort für Ihren creategy-Cockpit-Zugang zurücksetzen." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: (search: Record<string, unknown>): { email?: string } => ({
    email: typeof search.email === "string" ? search.email.toLowerCase() : undefined,
  }),
  component: CockpitForgotPasswordPage,
});

function CockpitForgotPasswordPage() {
  const search = Route.useSearch();
  const [email, setEmail] = useState(search.email ?? "");
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileHandle>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = z
      .string()
      .trim()
      .toLowerCase()
      .email("Bitte eine gültige E-Mail-Adresse eingeben.")
      .max(255)
      .safeParse(email);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Eingabe ungültig.");
      return;
    }
    if (!turnstileToken) {
      setError("Bitte schließen Sie die Bot-Schutz-Prüfung ab.");
      return;
    }
    setBusy(true);
    try {
      const outcome = await requestAccessLink({
        data: {
          email: parsed.data,
          area: "cockpit",
          origin: window.location.origin,
        },
      });
      if (outcome.ok) {
        setSent(outcome.message);
      } else {
        setError(outcome.message);
        turnstileRef.current?.reset();
        setTurnstileToken(null);
      }
    } catch {
      setError(
        "Wir konnten die E-Mail nicht senden. Bitte versuchen Sie es in wenigen Minuten erneut oder schreiben Sie an hello@creategy.de.",
      );
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex h-16 max-w-6xl items-center px-6 md:px-12">
          <Logo />
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-3xl font-extrabold">Passwort zurücksetzen</h1>

          {sent ? (
            <div className="mt-4 space-y-4">
              <p className="font-body text-sm text-muted-foreground">{sent}</p>
              <p className="font-body text-sm text-muted-foreground">
                Bitte prüfen Sie Ihr Postfach – auch den Spam-Ordner. Der Link ist nur
                begrenzte Zeit gültig.
              </p>
              <Link
                to="/cockpit/login"
                search={{ redirect: "/cockpit" }}
                className="inline-flex items-center rounded-md bg-brand-button px-4 py-2.5 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90"
              >
                Zurück zum Login
              </Link>
            </div>
          ) : (
            <>
              <p className="mt-2 font-body text-sm text-muted-foreground">
                Geben Sie die E-Mail-Adresse Ihres Cockpit-Zugangs ein. Wir senden Ihnen
                einen Link, mit dem Sie ein neues Passwort festlegen können.
              </p>

              <form onSubmit={onSubmit} className="mt-8 space-y-4" noValidate>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold">
                    E-Mail
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
                  />
                </div>

                <div className="space-y-2">
                  <TurnstileWidget action="login"
                    ref={turnstileRef}
                    context="login"
                    onToken={(token) => {
                      setTurnstileToken(token);
                      if (token) setTurnstileError(null);
                    }}
                    onError={(message) => setTurnstileError(message)}
                  />
                  {turnstileError && (
                    <p role="alert" className="text-xs text-destructive">
                      {turnstileError}
                    </p>
                  )}
                </div>

                {error && (
                  <p role="alert" className="text-sm text-destructive">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={busy || !turnstileToken}
                  className="w-full rounded-md bg-brand-button px-4 py-2.5 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
                >
                  {busy ? "Einen Moment…" : "Link senden"}
                </button>

                <Link
                  to="/cockpit/login"
                  search={{ redirect: "/cockpit" }}
                  className="block text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
                >
                  Zurück zum Login
                </Link>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
